import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BsX } from 'react-icons/bs';
import './RaiseAmountPopup.css';

interface StackSizePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (amount: number) => void;
  initialValue?: number;
  bigBlind?: number;
}

const StackSizePopup: React.FC<StackSizePopupProps> = ({ isOpen, onClose, onSave, initialValue, bigBlind }) => {
  const [amount, setAmount] = useState<string>('');
  const allowDecimals = typeof bigBlind === 'number' && !Number.isInteger(bigBlind) ? true : false;
  const formatValue = (num: number) => allowDecimals ? String(Number(num.toFixed(2))) : String(Math.round(num));

  useEffect(() => {
    if (isOpen) {
      // Clear for easier entry; presets will set a value
      setAmount('');
    }
  }, [isOpen, initialValue, bigBlind]);

  const handleSave = () => {
    const val = parseFloat(amount);
    if (!isNaN(val) && val >= 0) {
      const finalVal = allowDecimals ? val : Math.round(val);
      onSave(finalVal);
      onClose();
    } else {
      alert('Enter a valid stack amount.');
    }
  };

  const appendChar = (ch: string) => {
    setAmount(prev => {
      if (ch === '.') {
        if (!allowDecimals) return prev;
        if (!prev) return '0.';
        if (prev.includes('.')) return prev;
        return prev + '.';
      }
      if (prev === '0' && ch !== '.') return ch;
      const [i, d] = prev.split('.');
      if (allowDecimals && d && d.length >= 2) return prev;
      return (prev || '') + ch;
    });
  };
  const backspace = () => setAmount(prev => prev.slice(0, -1));
  const clearAll = () => setAmount('');

  const getSuggestions = () => {
    const suggestions: Array<{ label: string; value: number }>=[];
    if (bigBlind && bigBlind > 0) {
      suggestions.push({ label: '50 BB', value: bigBlind * 50 });
      suggestions.push({ label: '100 BB', value: bigBlind * 100 });
      suggestions.push({ label: '150 BB', value: bigBlind * 150 });
      suggestions.push({ label: '200 BB', value: bigBlind * 200 });
    }
    const cur = parseFloat(amount||'0') || 0;
    suggestions.push({ label: '+$10', value: cur + 10 });
    suggestions.push({ label: '+$25', value: cur + 25 });
    suggestions.push({ label: '+$50', value: cur + 50 });
    return suggestions;
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="raise-dialog-overlay" />
        <Dialog.Content className="raise-dialog-content">
          <Dialog.Title className="raise-dialog-title">Set Stack Size</Dialog.Title>
          <div className="raise-input-container">
            <div className="raise-amount-display">
              <span className="currency">$</span>
              <span className="value">{amount || '0'}</span>
            </div>
            {bigBlind && bigBlind > 0 && (
              <div className="raise-meta">
                <span>BB: ${bigBlind.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div className="suggested-amounts">
            {getSuggestions().map(s => (
              <button key={s.label} className="suggested-amount-btn" onClick={() => setAmount(formatValue(s.value))}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="numpad-grid">
            {['1','2','3','4','5','6','7','8','9'].map(n => (
              <button key={n} className="numpad-key" onClick={() => appendChar(n)}>{n}</button>
            ))}
            <button className="numpad-key" onClick={() => appendChar('.')}>.</button>
            <button className="numpad-key" onClick={() => appendChar('0')}>0</button>
            <button className="numpad-key backspace" onClick={backspace}>⌫</button>
            <button className="numpad-key clear" onClick={clearAll}>CLR</button>
          </div>

          <div className="raise-dialog-actions">
            <button className="raise-dialog-button raise" onClick={handleSave}>Save</button>
          </div>

          <Dialog.Close asChild>
            <button className="raise-dialog-close" aria-label="Close">
              <BsX />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default StackSizePopup;


